/**
 * Money Flow Index (MFI)
 *
 * Volume-weighted counterpart to RSI.  Uses typical price and volume to
 * measure the rate of money flowing in/out of a security.
 * Ranges 0–100; overbought ≥80, oversold ≤20.
 * Default period: 14.
 *
 * Category: Volume | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";

export const mfiIndicator: IndicatorDefinition = {
  id: "mfi",
  name: "Money Flow Index",
  shortName: "MFI",
  category: "volume",
  pane: "separate",
  params: [
    { key: "period", label: "Period", type: "number", default: 14, min: 1, max: 200 },
    { key: "overbought", label: "Overbought", type: "number", default: 80, min: 50, max: 100 },
    { key: "oversold", label: "Oversold", type: "number", default: 20, min: 0, max: 50 },
    { key: "color", label: "Color", type: "color", default: "#26C6DA" },
  ],
  plots: [
    { key: "mfi", label: "MFI", color: "#26C6DA", style: "line", lineWidth: 2, priceScaleId: "mfi" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period } = params;
    if (candles.length < period + 1) return [{ key: "mfi", data: [] }];

    const data: { time: number; value: number }[] = [];

    // Typical price = (H + L + C) / 3
    const typicalPrices = candles.map(c => (c.high + c.low + c.close) / 3);
    // Raw Money Flow = TP * Volume
    const rawMF = typicalPrices.map((tp, i) => tp * (candles[i].volume || 0));

    for (let i = period; i < candles.length; i++) {
      let positiveMF = 0;
      let negativeMF = 0;

      for (let j = i - period + 1; j <= i; j++) {
        if (typicalPrices[j] > typicalPrices[j - 1]) {
          positiveMF += rawMF[j];
        } else if (typicalPrices[j] < typicalPrices[j - 1]) {
          negativeMF += rawMF[j];
        }
      }

      const mfi = negativeMF === 0 ? 100 : 100 - 100 / (1 + positiveMF / negativeMF);
      data.push({ time: candles[i].time, value: mfi });
    }

    return [{ key: "mfi", data }];
  },
};
